import React from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import {
  ModalPageView,
  type ModalItem,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { toast } from '@cotera/client/app/components/ui';
import {
  graphHasEnabledTriggerBinding,
  workflowCanvasHasNodeSelection,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';
import { resolveWorkflowEntryRunInputSpec } from './workflow-entry-run-input';
import { WorkflowTriggerWorkflowAskView } from './workflow-trigger-workflow-ask-view';

type ModalManagerActions = {
  push: (item: ModalItem) => void;
  pop: () => void;
};

const TRIGGER_WORKFLOW_MODAL_SIZE = {
  custom: 'w-[92vw] max-w-lg rounded-lg',
} as const;

const triggerWorkflowParamsSchema = z.object({
  runInput: z.unknown(),
});

type Params = z.infer<typeof triggerWorkflowParamsSchema>;

export class WorkflowToolbarTriggerWorkflowAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Trigger workflow';
  description = 'Run this workflow manually with custom inputs';
  inputSchema = triggerWorkflowParamsSchema;
  group = 'Workflow';
  icon = 'play';

  constructor(
    private readonly modalManager: ModalManagerActions,
    private readonly onTestStarted?: () => void
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const model = workflowModelFromContext(context);
    if (model === null) {
      return false;
    }
    if (workflowCanvasHasNodeSelection(context)) {
      return false;
    }
    const graph = model.graphWatchable.snapshot();
    if (graph === null || graph.nodes.length === 0) {
      return false;
    }
    if (graphHasEnabledTriggerBinding(graph)) {
      return false;
    }
    return true;
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  ask(
    _params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const model = workflowModelFromContext(context);
    const graph = model?.graphWatchable.snapshot() ?? null;
    const resolution =
      graph === null
        ? ({ ok: true, spec: { kind: 'empty' as const } } as const)
        : resolveWorkflowEntryRunInputSpec(graph);

    if (resolution.ok === false) {
      return {
        view: ({ onCancel }) => {
          toast.error(
            'Entry nodes have conflicting input types — cannot collect run input'
          );
          void onCancel();
        },
      };
    }

    const spec = resolution.spec;

    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          size: TRIGGER_WORKFLOW_MODAL_SIZE,
          view: () => (
            <ModalPageView className="flex-1 flex-col px-5 py-5 sm:px-8 sm:py-6">
              <WorkflowTriggerWorkflowAskView
                spec={spec}
                onRespond={async (runInput) => {
                  await onRespond({ runInput });
                  this.modalManager.pop();
                }}
                onCancel={handleCancel}
              />
            </ModalPageView>
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = workflowModelFromContext(context);
    if (model === null) {
      toast.error('Could not start workflow run');
      return err([{ t: 'error', message: 'Workflow model not in scope' }]);
    }

    const triggerResult = await model.triggerWorkflow({
      runInput: payload.runInput,
    });
    if (triggerResult.success === false) {
      toast.error(triggerResult.message);
      return err([{ t: 'error', message: triggerResult.message }]);
    }

    toast.success('Workflow run started — check the Runs panel');
    this.onTestStarted?.();
    return ok({});
  }
}
